import { create } from 'zustand';

export type TaskDirection = 'outgoing' | 'incoming';
export type TaskStatus = 'submitted' | 'working' | 'completed' | 'failed' | 'cancelled' | 'pending_approval' | 'rejected';

export interface AsyncTask {
    taskId: string;
    direction: TaskDirection;
    status: TaskStatus;

    // For outgoing: remote agent we delegated to
    // For incoming: remote agent that delegated to us
    remoteAgent: string;
    remoteAgentName?: string;

    // Request details
    request: {
        text: string;
        metadata?: Record<string, unknown>;
    };

    // Response (when completed)
    response?: {
        text: string;
        metadata?: Record<string, unknown>;
    };

    // Error message (if failed)
    error?: string;

    // Session ID (for outgoing: our session that initiated; for incoming: will be created on accept)
    sessionId?: string;

    // Timestamps
    createdAt: string;
    updatedAt: string;
    completedAt?: string;
}

interface TasksStore {
    // State
    tasks: Map<string, AsyncTask>;
    autoAcceptIncoming: boolean;

    // Actions
    addTask: (task: AsyncTask) => void;
    updateTask: (taskId: string, updates: Partial<AsyncTask>) => void;
    removeTask: (taskId: string) => void;

    // Getters
    getOutgoingTasks: () => AsyncTask[];
    getIncomingTasks: () => AsyncTask[];
    getPendingIncomingCount: () => number;

    // Settings
    setAutoAcceptIncoming: (autoAccept: boolean) => void;

    // Clear
    clearTasks: () => void;
}

export const useTasksStore = create<TasksStore>((set, get) => ({
    // Initial state
    tasks: new Map(),
    autoAcceptIncoming: false,

    // Actions
    addTask: (task) => {
        set((state) => {
            const newTasks = new Map(state.tasks);
            newTasks.set(task.taskId, task);
            return { tasks: newTasks };
        });
    },

    updateTask: (taskId, updates) => {
        set((state) => {
            const newTasks = new Map(state.tasks);
            const existing = newTasks.get(taskId);
            if (existing) {
                newTasks.set(taskId, { ...existing, ...updates, updatedAt: new Date().toISOString() });
            }
            return { tasks: newTasks };
        });
    },

    removeTask: (taskId) => {
        set((state) => {
            const newTasks = new Map(state.tasks);
            newTasks.delete(taskId);
            return { tasks: newTasks };
        });
    },

    // Getters
    getOutgoingTasks: () => {
        const { tasks } = get();
        return Array.from(tasks.values())
            .filter(t => t.direction === 'outgoing')
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    },

    getIncomingTasks: () => {
        const { tasks } = get();
        return Array.from(tasks.values())
            .filter(t => t.direction === 'incoming')
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    },

    getPendingIncomingCount: () => {
        const { tasks } = get();
        return Array.from(tasks.values()).filter(
            t => t.direction === 'incoming' && t.status === 'pending_approval'
        ).length;
    },

    // Settings
    setAutoAcceptIncoming: (autoAccept) => {
        set({ autoAcceptIncoming: autoAccept });
    },

    // Clear
    clearTasks: () => {
        set({ tasks: new Map() });
    },
}));
